import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Typography,
  Alert,
  Card,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useMutation, useQuery } from "@apollo/client";
import {
  ADMIN_COLLECTIONS_QUERY,
  ADMIN_DELETE_COLLECTION,
  ADMIN_UPSERT_COLLECTION,
} from "../../graphql/collections";
import { uploadCollectionImages } from "../../api/collectionImages";
import { buildImageUrl } from "../../config";

type CollectionForm = {
  id?: string;
  name: string;
  slug?: string;
  description?: string;
  images: string[];
};

const emptyForm: CollectionForm = {
  name: "",
  slug: "",
  description: "",
  images: [],
};

export default function AdminCollectionsPage() {
  const { data, loading, error, refetch } = useQuery(ADMIN_COLLECTIONS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const [upsert] = useMutation(ADMIN_UPSERT_COLLECTION);
  const [del] = useMutation(ADMIN_DELETE_COLLECTION);

  const collections = useMemo(() => data?.adminCollections ?? [], [data]);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CollectionForm>(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [uiError, setUiError] = useState<string>("");

  const openCreate = () => {
    setUiError("");
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c: any) => {
    setUiError("");
    setForm({
      id: c.id,
      name: c.name ?? "",
      slug: c.slug ?? "",
      description: c.description ?? "",
      images: c.images ?? [],
    });
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setUiError("");
  };

  const onPickImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUiError("");
    setUploading(true);
    try {
      const paths = await uploadCollectionImages(Array.from(files));
      setForm((p) => ({ ...p, images: [...(p.images || []), ...paths] }));
    } catch (e: any) {
      setUiError(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (path: string) => {
    setForm((p) => ({
      ...p,
      images: (p.images || []).filter((x) => x !== path),
    }));
  };

  const save = async () => {
    setSaving(true);
    setUiError("");

    try {
      const res = await upsert({
        variables: {
          id: form.id,
          name: form.name.trim(),
          slug: form.slug?.trim() || null,
          description: form.description?.trim() || null,
          images: form.images,
        },
      });

      const errs = res?.data?.adminUpsertCollection?.errors ?? [];
      if (errs.length) throw new Error(errs.join(", "));

      setOpen(false);
      await refetch();
    } catch (e: any) {
      setUiError(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this collection?")) return;
    try {
      const res = await del({ variables: { id } });
      const errs = res?.data?.adminDeleteCollection?.errors ?? [];
      if (errs.length) throw new Error(errs.join(", "));
      await refetch();
    } catch (e: any) {
      alert(e?.message || "Failed to delete");
    }
  };

  return (
    <Box sx={{ py: 5 }}>
      <Container maxWidth="lg">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
              Collections
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 14 }}>
              Create collections with images and descriptions.
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={openCreate}
            sx={{ borderRadius: 0 }}
          >
            New Collection
          </Button>
        </Stack>

        {error && <Alert severity="error">Failed to load collections.</Alert>}
        {loading && <Typography color="text.secondary">Loading…</Typography>}

        <Stack spacing={2}>
          {collections.map((c: any) => {
            const cover = c.images?.[0] ? buildImageUrl(c.images[0]) : "";
            return (
              <Card key={c.id} sx={{ borderRadius: 0, p: 2 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: { xs: "100%", md: 260 },
                      height: 160,
                      border: "1px solid rgba(0,0,0,0.12)",
                      overflow: "hidden",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "#fafafa",
                    }}
                  >
                    {cover ? (
                      <Box
                        component="img"
                        loading="lazy"
                        decoding="async"
                        src={cover}
                        alt={c.name}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography color="text.secondary">No image</Typography>
                    )}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
                      {c.name}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      /collections/{c.slug}
                    </Typography>
                    {c.description && (
                      <Typography
                        sx={{ mt: 1, fontSize: 14, color: "text.secondary" }}
                      >
                        {c.description}
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <IconButton onClick={() => openEdit(c)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => remove(c.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Container>

      {/* Form Dialog */}
      <Dialog open={open} onClose={close} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {form.id ? "Edit Collection" : "New Collection"}
        </DialogTitle>
        <DialogContent dividers>
          {uiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uiError}
            </Alert>
          )}

          <Stack spacing={2}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Slug (optional)"
              helperText="Leave empty to auto-generate from name"
              value={form.slug ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description ?? ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              fullWidth
              multiline
              minRows={4}
            />

            <Divider />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={{ fontWeight: 800 }}>Images</Typography>

              <Button
                component="label"
                startIcon={<AddPhotoAlternateIcon />}
                variant="outlined"
                sx={{ borderRadius: 0 }}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                    Uploading…
                  </>
                ) : (
                  "Upload"
                )}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    onPickImages(e.target.files);
                    e.currentTarget.value = ""; // allow selecting same file again
                  }}
                />
              </Button>
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={2}>
              {(form.images || []).map((img) => {
                const src = buildImageUrl(img);
                return (
                  <Box key={img} sx={{ width: 180 }}>
                    <Box
                      sx={{
                        width: 180,
                        height: 130,
                        border: "1px solid rgba(0,0,0,0.12)",
                        overflow: "hidden",
                        position: "relative",
                        bgcolor: "#fafafa",
                      }}
                    >
                      <Box
                        component="img"
                        loading="lazy"
                        decoding="async"
                        src={src}
                        alt="collection"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        onClick={() => removeImage(img)}
                        size="small"
                        disabled={uploading}
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          bgcolor: "rgba(255,255,255,0.85)",
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography
                      sx={{ mt: 0.5, fontSize: 11, color: "text.secondary" }}
                      noWrap
                    >
                      {img}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || uploading || !form.name.trim()}
            sx={{ borderRadius: 0 }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
